import { Helmet } from '@dr.pogodin/react-helmet';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-typed-form';

import { API_ROOT } from '../../config';
import { FormWrap } from '../../form';
import { createFormValidator } from '../../form';
import AuthBoxWrap from './AuthBoxWrap';
import AuthHeading from './AuthHeading';
import AuthMessageLink from './AuthMessageLink';
import AuthMessageWrap from './AuthMessageWrap';
import AuthSubmitButton from './AuthSubmitButton';
import LoginField from './LoginField';

export default function ResetPasswordPage() {
  const params = useParams<{ email: string; hash: string }>();
  const email = decodeURIComponent(params.email ?? '');
  const hash = params.hash ?? '';

  const [hashValid, setHashValid] = React.useState<boolean | null>(null);
  const [success, setSuccess] = React.useState<boolean>(false);

  React.useEffect(() => {
    async function checkHash() {
      const response = await fetch(`${API_ROOT}/forgot-password/check-hash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, hash }),
      });
      if (!response.ok) {
        setHashValid(false);
        throw new Error(`Bad status: ${response.status}`);
      }
      setHashValid(true);
    }
    checkHash();
  }, []);

  const { formErrorList, getField, handleSubmit, isLoading } = useForm<{
    plainPassword: string;
  }>({
    defaultValues: { plainPassword: '' },
    validator: createFormValidator({
      plainPassword: {
        strictPresence: true,
        length: { minimum: 10 },
        passwordFormat: true,
      },
    }),
    onSubmit: async ({ plainPassword }, { setLoading }) => {
      setLoading(true);
      try {
        const response = await fetch(`${API_ROOT}/forgot-password/reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, hash, plainPassword }),
        });
        if (!response.ok) throw new Error(`Bad status: ${response.status}`);
        setSuccess(true);
      } catch (err: unknown) {
        setLoading(false);
        alert('Something went wrong');
        throw err;
      }
    },
  });

  if (!email || !hash) {
    throw new Error('nav param missing');
  }

  if (hashValid === null) {
    return <AuthMessageWrap>Checking link...</AuthMessageWrap>;
  }

  if (hashValid === false) {
    return (
      <AuthMessageWrap>
        The link you followed was not valid. It may have expired, or already
        been used.
      </AuthMessageWrap>
    );
  }

  if (success) {
    return (
      <AuthMessageWrap>
        The password for {email} has been reset, you may now{' '}
        <AuthMessageLink to="/login">log in</AuthMessageLink>.
      </AuthMessageWrap>
    );
  }

  return (
    <AuthBoxWrap>
      <Helmet>
        <title>Reset your Password</title>
      </Helmet>
      <AuthHeading>Reset your Password</AuthHeading>
      <FormWrap formErrorList={formErrorList} handleSubmit={handleSubmit}>
        <LoginField
          type="password"
          field={{ ...getField('plainPassword'), label: 'New Password' }}
          autoFocus
        />

        <AuthSubmitButton disabled={isLoading}>Save</AuthSubmitButton>
      </FormWrap>
    </AuthBoxWrap>
  );
}
